import { field, pathIsKnown } from "../content/model";
import type { EntityType, FieldType } from "../content/model";
import { REGISTRY, REGISTRY_VERSION } from "./registry";
import type { PropType } from "./registry";
import { isBinding } from "./spec";
import type { PropValue, SpecNode, ViewSpec } from "./spec";

// EVERY SPEC IS CHECKED BEFORE IT RENDERS, whoever produced it.
//
// The default producer's output is checked too, and that is not paranoia about our own code: the
// check is the contract a GENERATED spec will be held to, and a contract only the untrusted
// producer has to satisfy is one nobody has tested. If the default producer cannot pass its own
// validator, the validator is wrong.
//
// A spec that fails does not render. The caller falls back to the default view for that context
// and logs the errors loudly — a half-drawn screen that silently omits whatever was invalid is
// the one outcome worse than either.

export interface SpecError {
  /** Where in the tree: `root.children[2].props.text`. */
  at: string;
  message: string;
}

/** Which content field types may be bound into which prop types.
 *
 *  Asymmetric on purpose: a number reads fine in a text prop, and so does a handle. A list never
 *  reads as text — `unit.standards` bound into a label is the kind of mistake that renders
 *  "[object Object]" and looks like a styling bug for a day. */
const ACCEPTS: Record<PropType, FieldType[]> = {
  text: ["text", "number", "handle", "prose"],
  prose: ["prose", "text"],
  number: ["number"],
  boolean: [],
  text_list: ["text_list"],
  standards: ["standards"],
  blocks: ["blocks"],
  resources: ["resources"],
};

export function validateSpec(spec: ViewSpec): SpecError[] {
  const errors: SpecError[] = [];

  if (spec.registryVersion !== REGISTRY_VERSION) {
    errors.push({
      at: "registryVersion",
      message: `spec was produced against registry v${spec.registryVersion}, this is `
        + `v${REGISTRY_VERSION} — a component it names may no longer exist`,
    });
  }
  if (!spec.entityType || !spec.root) {
    errors.push({ at: "root", message: "a spec needs an entityType and a root node" });
    return errors;
  }
  walk(spec.root, "root", spec.entityType, errors);
  return errors;
}

function walk(node: SpecNode, at: string, entity: EntityType, errors: SpecError[]): void {
  const spec = REGISTRY[node.component];
  if (!spec) {
    errors.push({ at, message: `unknown component "${node.component}"` });
    return;
  }

  if (node.$when && !pathIsKnown(entity, node.$when.present)) {
    errors.push({
      at: `${at}.$when`,
      message: `${node.$when.present} is not a field of a ${entity}`,
    });
  }

  for (const [name, declared] of Object.entries(spec.props)) {
    const given = node.props?.[name];
    if (declared.required && (given === undefined || given === null)) {
      errors.push({ at: `${at}.props.${name}`, message: `${node.component} requires ${name}` });
    }
  }

  for (const [name, value] of Object.entries(node.props ?? {})) {
    const declared = spec.props[name];
    if (!declared) {
      errors.push({
        at: `${at}.props.${name}`,
        message: `${node.component} has no prop called ${name}`,
      });
      continue;
    }
    checkValue(value, declared.type, `${at}.props.${name}`, entity, node.component, errors);
  }

  const children = node.children ?? [];
  if (children.length > 0 && !spec.children) {
    errors.push({ at, message: `${node.component} does not take children` });
  }
  children.forEach((child, i) => walk(child, `${at}.children[${i}]`, entity, errors));
}

function checkValue(value: PropValue, want: PropType, at: string, entity: EntityType,
                    component: string, errors: SpecError[]): void {
  if (isBinding(value)) {
    const path = value.$bind;
    if (!pathIsKnown(entity, path)) {
      errors.push({ at, message: `${path} is not a field of a ${entity}` });
      return;
    }
    // A path into a field's interior — `context.courseName`, `standards.0.code` — is allowed and
    // not type-checked: the schema describes fields, not the shape inside one, and guessing would
    // reject bindings that are correct.
    const parts = path.split(".").filter((p, i) => !(i === 0 && p === entity));
    if (parts.length !== 1) return;
    const f = field(entity, parts[0]);
    if (!f) return;
    if (!ACCEPTS[want].includes(f.type)) {
      errors.push({
        at,
        message: `${component}.${at.split(".").pop()} wants ${want}, but ${path} is ${f.type}`,
      });
    }
    return;
  }
  if (value === null || value === undefined) return;
  const t = typeof value;
  const ok = want === "number" ? t === "number"
    : want === "boolean" ? t === "boolean"
    : want === "text" || want === "prose" ? t === "string" || t === "number"
    : Array.isArray(value);
  if (!ok) {
    errors.push({ at, message: `${component} wants ${want} here, got ${t}` });
  }
}
